/**
 * Vendor opt-outs that tell password-manager **browser extensions** to leave
 * a field alone.
 *
 * Extensions do not read the WHATWG `autocomplete` attribute — that only
 * governs the browser's own autofill. Each vendor runs its own DOM
 * classification heuristics, and a bare `type="text"` input on an
 * auth-looking route gets classified as a username field regardless of
 * what `autocomplete` says (LastPass filled a saved email into the
 * confirmation-code field on `/confirm-signup`, #2848). Each vendor
 * documents its own `data-*` attribute as the opt-out, and each is inert
 * to every other vendor, so they are always applied together.
 *
 * Sources, checked 2026-09-19:
 * - LastPass — `data-lpignore="true"`; the literal value `"true"` is
 *   required. https://support.lastpass.com/s/document-item?bundleId=lastpass&topicId=LastPass/t_disable_autofill_specific_field.html
 * - 1Password — `data-1p-ignore`, presence-checked.
 *   https://www.1password.dev/web/compatible-website-design/
 * - Bitwarden — `data-bwignore`, presence-checked
 *   (`:not([data-bwignore])` in `collect-autofill-content.service.ts`).
 *   Best-effort: the extension only honours it behind a per-user opt-in
 *   that defaults to off and whose settings toggle is itself dev-flagged,
 *   so release builds may not honour it yet.
 * - Dashlane — `data-form-type="other"`, from the Semantically Annotated
 *   Web Forms spec: "Should you need to make a form field 'ignored' by
 *   client codes, you can set its `data-form-type` attribute to `other`."
 *   https://dashlane.github.io/SAWF/
 * - Proton Pass — `data-protonpass-ignore`, presence-checked
 *   (`el.getAttribute('data-protonpass-ignore') !== null` in
 *   `packages/pass/fathom/index.js`).
 *
 * These must be present at first paint: LastPass caches its classification
 * of a form after the initial scan, so an attribute added later by an
 * effect or a conditional render often will not un-fill an already-filled
 * field. Spread them in the initial JSX, never from a mount effect.
 *
 * @example
 * // Applied last so a caller cannot clobber them by accident.
 * <input {...rest} {...PASSWORD_MANAGER_IGNORE_ATTRIBUTES} />
 */
export const PASSWORD_MANAGER_IGNORE_ATTRIBUTES = {
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
  'data-protonpass-ignore': 'true',
} as const satisfies Record<`data-${string}`, string>
